import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { useBilling } from "@/context/BillingContext";
import { SECTIONS, SIM_BY_ID, isSimNew } from "@/data/simulators";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import { ArrowRight, GraduationCap, LogOut, LogIn, ChevronDown, Shield, Users, Lock, BookOpen, Clock, FileText } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SUBSCRIPTIONS_ENABLED } from "@/lib/featureFlags";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtEtaChip = (val) => {
  if (typeof val !== "string") return null;
  const m = val.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const mo = MONTHS_SHORT[parseInt(m[2], 10) - 1];
  if (!mo) return null;
  const year = parseInt(m[1], 10);
  const currentYear = new Date().getFullYear();
  return year === currentYear ? mo : `${mo} '${String(year).slice(-2)}`;
};

export default function Home() {
  const navigate = useNavigate();
  const { user, authReady, logout } = useAuth();
  const { isSimVisible, isSimFree, getSimEta } = useContent();
  const { entitlement, billingReady } = useBilling();
  const isAdmin = user?.role === "admin";
  // Wait for billingReady before flagging a signed-in user as "restricted"; a
  // paying user would otherwise briefly see "Pro/locked" chips before their
  // entitlement loads. Signed-out users are always restricted.
  // When SUBSCRIPTIONS_ENABLED is false nobody is restricted — every sim is free.
  const restricted = SUBSCRIPTIONS_ENABLED
    && !isAdmin
    && (!user || (billingReady && entitlement?.access === "free"));

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">
              Veterinary clinical simulation training
            </span>
          </Link>
          {authReady && (
            <div className="flex items-center gap-2" data-testid="home-auth-controls">
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="nav-admin-btn">
                      <Button variant="outline" size="sm" className="text-[#0047AB] border-[#0047AB]/40">
                        <Shield className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">Admin</span>
                      </Button>
                    </Link>
                  )}
                  {user.role === "instructor" && (
                    <Link to="/team" data-testid="nav-team-btn">
                      <Button variant="outline" size="sm" className="text-[#0047AB] border-[#0047AB]/40">
                        <Users className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">My team</span>
                      </Button>
                    </Link>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="user-menu-trigger"
                        className="text-slate-700 gap-2 pl-1.5 pr-2 hover:bg-slate-100"
                      >
                        <span className="w-7 h-7 rounded-full bg-[#0047AB]/10 text-[#0047AB] flex items-center justify-center text-xs font-bold uppercase">
                          {(user.name || user.email || "?").trim().charAt(0)}
                        </span>
                        <span className="hidden md:inline max-w-[160px] truncate text-sm" data-testid="home-user-name">
                          {user.name || user.email}
                        </span>
                        <ChevronDown className="w-4 h-4 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60" data-testid="user-menu">
                      <DropdownMenuLabel className="font-normal">
                        <div className="text-sm font-semibold text-slate-900 truncate">{user.name || "Signed in"}</div>
                        <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate("/dashboard")}
                        data-testid="nav-dashboard-btn"
                        className="cursor-pointer"
                      >
                        <GraduationCap className="w-4 h-4 mr-2 text-[#0047AB]" />
                        My scores &amp; sessions
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href="/CertIVSims-Learner-Guide.pdf" download data-testid="nav-learner-guide-btn">
                          <BookOpen className="w-4 h-4 mr-2 text-[#0047AB]" />
                          Quick-start guide (PDF)
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => logout()}
                        data-testid="home-logout-btn"
                        className="cursor-pointer text-slate-600"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Link to="/login" data-testid="nav-login-btn">
                  <Button size="sm" className="bg-[#0047AB] hover:bg-[#003380] text-white">
                    <LogIn className="w-4 h-4 mr-1.5" /> Sign in
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 fade-up">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
          <GraduationCap className="w-3.5 h-3.5" />
          <span>Training suite</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
          Choose a section
        </h1>
        <p className="text-base text-slate-600 mt-3 max-w-2xl">
          Pick a clinical area to see its simulators. Sign in to save and track your
          simulator <span className="font-semibold text-slate-900">scores and grades</span> for
          every completed simulation.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {SECTIONS.map((s) => {
            const Icon = s.Icon;
            const activeSims = s.sims.filter(isSimVisible);
            const soonSims = s.sims.filter((id) => !isSimVisible(id));
            const count = activeSims.length;
            const freeCount = restricted ? activeSims.filter(isSimFree).length : count;
            const lockedCount = count - freeCount;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => navigate(`/section/${s.id}`)}
                data-testid={`section-tile-${s.id}`}
                className="group text-left bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col"
                style={{ borderTopWidth: 3, borderTopColor: s.accent }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center text-white shrink-0"
                    style={{ background: s.accent }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-2xl font-bold tabular-nums"
                    style={{ color: s.accent }}
                  >
                    {String(s.n).padStart(2, "0")}
                  </span>
                </div>

                <h2 className="text-lg font-semibold text-slate-900">{s.title}</h2>
                <p className="text-sm text-slate-600 leading-relaxed mt-1.5 flex-1">
                  {s.blurb}
                </p>

                <div className="flex flex-wrap gap-1.5 mt-4 min-h-[24px]">
                  {s.sims.length > 0 ? (
                    <>
                      {activeSims.map((id) => {
                        const simLocked = restricted && !isSimFree(id);
                        const simNew = isSimNew(id);
                        return (
                          <span
                            key={id}
                            data-testid={simNew ? `sim-new-chip-${id}` : undefined}
                            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded ${
                              simLocked
                                ? "bg-slate-100 text-slate-400"
                                : simNew
                                ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                                : restricted
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {simLocked && <Lock className="w-2.5 h-2.5" />}
                            {SIM_BY_ID[id].title}
                            {simNew && (
                              <span className="ml-1 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                                new
                              </span>
                            )}
                          </span>
                        );
                      })}
                      {soonSims.map((id) => {
                        const etaLabel = fmtEtaChip(getSimEta(id));
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded bg-amber-50 text-amber-700"
                            data-testid={`sim-soon-chip-${id}`}
                          >
                            <Clock className="w-2.5 h-2.5" />
                            {SIM_BY_ID[id].title}
                            {etaLabel && (
                              <span
                                className="text-amber-800/80 font-bold ml-0.5"
                                data-testid={`sim-soon-eta-${id}`}
                              >
                                · {etaLabel}
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </>
                  ) : (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid={`section-coming-soon-${s.id}`}
                    >
                      Coming soon
                    </span>
                  )}
                </div>

                <div
                  className="flex items-center gap-1.5 mt-5 text-sm font-semibold"
                  style={{ color: s.accent }}
                >
                  {s.sims.length === 0
                    ? "Preview section"
                    : restricted
                    ? `Open section · ${freeCount} free${lockedCount > 0 ? ` · ${lockedCount} Pro` : ""}${soonSims.length > 0 ? ` · ${soonSims.length} soon` : ""}`
                    : `Open section · ${count} simulator${count === 1 ? "" : "s"}${soonSims.length > 0 ? ` · ${soonSims.length} soon` : ""}`}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Free study companions — flashcards + printable cheat sheets */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <button
            type="button"
            onClick={() => navigate("/flashcards")}
            data-testid="flashcards-entry-tile"
            className="group text-left bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex flex-col sm:flex-row sm:items-center gap-5"
            style={{ borderTopWidth: 3, borderTopColor: "#0047AB" }}
          >
            <div className="w-12 h-12 rounded-lg bg-[#0047AB] text-white flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="text-lg font-semibold text-slate-900">Study Flashcards</h2>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Free
                </span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">
                Bite-sized study cards for every clinical area — flip, mark <b>Got it</b>, keep the tricky ones in rotation.
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0047AB] shrink-0">
              Open
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigate("/cheat-sheets")}
            data-testid="cheat-sheets-entry-tile"
            className="group text-left bg-white border border-slate-200 rounded-xl p-6 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 flex flex-col sm:flex-row sm:items-center gap-5"
            style={{ borderTopWidth: 3, borderTopColor: "#0f766e" }}
          >
            <div className="w-12 h-12 rounded-lg bg-[#0f766e] text-white flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h2 className="text-lg font-semibold text-slate-900">Quick Reference Cheat Sheets</h2>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Free · Printable
                </span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed mt-1">
                Vital ranges, drug doses, MAC values, fluid calcs, CPR algorithm, clinpath intervals — the numbers you look up every shift.
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-[#0f766e] shrink-0">
              Open
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      </main>
    </div>
  );
}
